/**
 * BMP Curated First — Between Reps Club port
 *
 * Pins a product to position #1 of a collection grid via URL parameters, so a
 * Meta catalog ad can land the shopper on a collection with the exact product
 * (and colour) they clicked already first.
 *
 * Ported 2026-09-30 from BuildMyPOD - Fitness v2.0.0 (assets/bmp-curated-first.js)
 * onto BuildMyPOD - Fitness v1.1.5 (Horizon 3.0.0 base). Same DOM contract:
 *   <results-list section-id="...">
 *     <ul ref="grid" class="product-grid ...">
 *       <li ref="cards[]" class="product-grid__item ..." data-page="N">
 *         <product-card> ... <swatches-variant-picker-component> ...
 *
 * What changed from v2, and why:
 *   1. The pinned card is the product's REAL card, not a stand-in. v2 fetched a
 *      hand-built card section (no swatches, fixed layout). Here, when the
 *      product isn't on the page already, the script asks
 *      templates/collection.bmp-first-index.liquid which page of this collection
 *      it is on, fetches that page of this same section through the Section
 *      Rendering API, and moves the genuine card to the front. It matches the
 *      grid exactly: gallery, title, price, swatches.
 *   2. Colour is picked the way a shopper would pick it: the script clicks the
 *      card's own colour swatch for the ad's variant, so Horizon updates the
 *      image, the link and the highlighted swatch itself. The v2 image swap
 *      only runs when the card has no matching swatch.
 *   3. The colour is applied ONCE per card. v2 re-applied the ad's variant on
 *      every re-render, which would undo a shopper choosing another colour on
 *      the pinned card.
 *   4. Page 1 only. Classic pagination links keep `?first=`, and without this
 *      the product would be pinned on top of every page the shopper opens.
 *   5. Config is on the loader tag in sections/main-collection.liquid (no theme
 *      settings): data-param, data-swap-image, data-per-page.
 *
 * URL params (param NAME configurable; default "first"):
 *   first   - product handle OR a full/encoded product URL (Meta deep link)
 *   variant - variant id (optional; also parsed from the product URL)
 */

const cfg = readConfig();

if (cfg.enabled) {
  const params = new URLSearchParams(window.location.search);
  const firstParam = params.get(cfg.param);

  if (firstParam) {
    const parsed = parseFirstParam(firstParam, params.get('variant'));

    if (parsed.handle) {
      init(parsed, cfg);
    }
  }
}

// ---- config ------------------------------------------------------------

function readConfig() {
  const el = document.querySelector('script[data-bmp-curated-first]');
  const d = (el && el.dataset) || {};
  return {
    enabled: el != null,
    param: d.param || 'first',
    swapImage: d.swapImage !== 'false',
    perPage: parseInt(d.perPage, 10) || 0,
  };
}

// ---- storefront routing ---------------------------------------------------

/** Storefront locale-root prefix (e.g. "/en-ca/"), always trailing-slashed. */
function shopifyRoot() {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  return root.charAt(root.length - 1) === '/' ? root : root + '/';
}

/**
 * This collection's URL with the shopper's sort/filter params, minus the params
 * this script owns. Both lookups in fetchAndPin() must see the same product
 * order as the grid, so they reuse whatever sort or filter the page itself has.
 */
function collectionUrl(cfg, extra) {
  const url = new URL(window.location.pathname, window.location.origin);
  const current = new URLSearchParams(window.location.search);
  current.forEach((value, key) => {
    if (key === cfg.param || key === 'variant' || key === 'page' || key === 'view' || key === 'section_id') return;
    url.searchParams.append(key, value);
  });
  Object.keys(extra).forEach((key) => url.searchParams.set(key, extra[key]));
  return url.pathname + url.search;
}

// ---- param parsing -------------------------------------------------------

/**
 * `first` may be a plain handle or a full/encoded product URL. Also extracts
 * the variant from the URL when not passed separately.
 *
 * `URLSearchParams#get()` already percent-decodes once, so `firstParam` is
 * normally plain text. Some ad-link generators double-encode the deep link, so
 * decode ONE more time only when the value doesn't already look like a URL, and
 * only use the result if that decode reveals one. try/catch because a handle
 * with a literal "%" is not valid percent-encoding.
 */
function parseFirstParam(firstParam, fallbackVariant) {
  let handle = firstParam;
  let variant = fallbackVariant || null;

  let candidate = firstParam;
  if (!/^https?:\/\//.test(candidate)) {
    try {
      const maybeDecoded = decodeURIComponent(candidate);
      if (/^https?:\/\//.test(maybeDecoded)) candidate = maybeDecoded;
    } catch (e) {
      // Not valid percent-encoding: treat as a plain handle below.
    }
  }

  if (/^https?:\/\//.test(candidate)) {
    try {
      // Full product URL (Meta deep link): pull the handle from /products/<handle>.
      const url = new URL(candidate);
      const parts = url.pathname.split('/');
      const idx = parts.indexOf('products');
      handle = idx !== -1 && parts[idx + 1] ? parts[idx + 1] : '';
      if (!variant) variant = new URLSearchParams(url.search).get('variant');
    } catch (e) {
      // Malformed URL: fall through and treat firstParam as a plain handle.
    }
  }

  handle = (handle || '').split('?')[0].split('#')[0].split('/').pop() || '';
  // Variant ids are always integers; strip anything else (defuse param pollution).
  variant = (variant || '').replace(/[^0-9]/g, '') || null;
  return { handle, variant };
}

// ---- grid helpers ---------------------------------------------------------

function getGrid(root) {
  const list = (root || document).querySelector('results-list');
  if (!list) return null;
  return list.querySelector('[ref="grid"]') || list.querySelector('.product-grid');
}

function getCards(grid) {
  return Array.prototype.slice.call(grid.querySelectorAll(':scope > [ref="cards[]"]'));
}

/** Does this card's product link point at the given handle? */
function cardMatchesHandle(card, handle) {
  const links = card.querySelectorAll('a[href*="/products/"]');
  for (let i = 0; i < links.length; i++) {
    if (linkMatchesHandle(links[i].getAttribute('href'), handle)) return true;
  }
  return false;
}

function linkMatchesHandle(href, handle) {
  if (!href) return false;
  const marker = '/products/' + handle;
  const at = href.indexOf(marker);
  if (at === -1) return false;
  // Guard against prefix collisions ("shirt" matching "shirt-blue"):
  // the char after the handle must end the path segment.
  const next = href.charAt(at + marker.length);
  return next === '' || next === '?' || next === '#' || next === '/';
}

function findCardByHandle(grid, handle) {
  const cards = getCards(grid);
  for (let i = 0; i < cards.length; i++) {
    if (cardMatchesHandle(cards[i], handle)) return cards[i];
  }
  return null;
}

/** Find a card matching `handle` other than `exclude` (duplicate detection). */
function findOtherCardWithHandle(grid, exclude, handle) {
  const cards = getCards(grid);
  for (let i = 0; i < cards.length; i++) {
    if (cards[i] !== exclude && cardMatchesHandle(cards[i], handle)) return cards[i];
  }
  return null;
}

// ---- pinning ---------------------------------------------------------------

/** Per-pin runtime state: shared by pin() and the re-render observer. */
function createState() {
  // `failed` latches an unresolvable handle so re-renders don't re-fetch it
  // forever; `inflight` stops a mutation that lands mid-fetch from firing a
  // second fetch; `variantInfo` caches the /products/<handle>.js lookup.
  return { pinning: false, cachedCard: null, rafId: 0, failed: false, inflight: false, variantInfo: null };
}

function init(parsed, cfg) {
  const state = createState();
  pin(parsed, cfg, state);
  observeReRenders(parsed, cfg, state);
}

function pin(parsed, cfg, state) {
  // The pin retires as soon as the shopper filters or sorts: the facets
  // re-render rebuilds the URL from the filter form only, which drops `?first=`.
  const params = new URLSearchParams(window.location.search);
  if (!params.get(cfg.param)) return;
  // Page 1 only (the ad lands there). Page links keep `?first=`.
  const page = params.get('page');
  if (page && page !== '1') return;

  const grid = getGrid();
  if (!grid || state.pinning) return;

  const firstCard = grid.querySelector(':scope > [ref="cards[]"]');
  if (firstCard && cardMatchesHandle(firstCard, parsed.handle)) {
    // Already pinned. If the genuine card for the same product has since
    // appeared elsewhere in the grid, prefer it and drop what we pinned.
    const duplicate = findOtherCardWithHandle(grid, firstCard, parsed.handle);
    if (duplicate) {
      state.pinning = true;
      try {
        // Read the displaced page BEFORE the remove, or moveToFront would
        // resolve the second <li> as the displaced card.
        const pageHint = firstCard.dataset.page;
        firstCard.remove();
        moveToFront(grid, duplicate, pageHint);
        applyVariant(duplicate, parsed, cfg, state);
      } finally {
        state.pinning = false;
      }
      return;
    }
    applyVariant(firstCard, parsed, cfg, state);
    return;
  }

  state.pinning = true;
  try {
    const existing = findCardByHandle(grid, parsed.handle);
    if (existing) {
      // Already on this page: swap slots rather than splice, so only two boxes
      // move instead of every card between position 1 and k.
      swapToFront(grid, existing);
      applyVariant(existing, parsed, cfg, state);
    } else if (state.cachedCard) {
      const clone = state.cachedCard.cloneNode(true);
      prioritizePinnedImage(clone);
      moveToFront(grid, clone);
      applyVariant(clone, parsed, cfg, state);
    } else if (!state.failed && !state.inflight) {
      fetchAndPin(parsed, cfg, state);
    }
  } finally {
    state.pinning = false;
  }
}

/**
 * Move `card` to the front of the grid, stamping its `data-page` from the card
 * it displaces. Horizon's card click writes `page` from the card's `li` into
 * history, and paginated-list.js reads cards[0].dataset.page, so the front
 * card must carry the front slot's page.
 *
 * The displaced card is found with `:scope > [ref="cards[]"]`, never
 * `firstElementChild`: on the storefront the grid's first child is the
 * grid-view <script> from snippets/product-grid.liquid, not a card.
 *
 * `pageHint` is for the duplicate branch in pin(), which removes the old front
 * card before calling this.
 */
function moveToFront(grid, card, pageHint) {
  const displaced = grid.querySelector(':scope > [ref="cards[]"]');
  if (displaced === card) return;
  const page = pageHint != null ? pageHint : displaced && displaced.dataset.page;
  if (page != null) card.dataset.page = page;
  grid.insertBefore(card, displaced || grid.firstElementChild);
}

/**
 * Swap `card` with the grid's current first card instead of splicing it in, so
 * a product already on the page moves only two boxes. `data-page` follows the
 * slot, not the card.
 */
function swapToFront(grid, card) {
  const first = grid.querySelector(':scope > [ref="cards[]"]');
  if (!first || first === card) return false;
  const after = card.nextSibling;
  const firstPage = first.dataset.page;
  const cardPage = card.dataset.page;
  grid.insertBefore(card, first);
  grid.insertBefore(first, after);
  if (firstPage != null) card.dataset.page = firstPage;
  if (cardPage != null) first.dataset.page = cardPage;
  return true;
}

/**
 * The pinned card is slot #1 on an ad landing, so it is the LCP candidate, but
 * a fetched card arrives late. Mark its main image eager + high priority before
 * it enters the document.
 */
function prioritizePinnedImage(card) {
  const img =
    card.querySelector('.card-gallery img') ||
    card.querySelector('.product-media-container img') ||
    card.querySelector('img');
  if (!img) return;
  img.setAttribute('loading', 'eager');
  img.setAttribute('fetchpriority', 'high');
}

/**
 * The product isn't on this page: find which page of this collection it is on,
 * fetch that page of this section, and pin its real card.
 */
function fetchAndPin(parsed, cfg, state) {
  // Marked BEFORE the fetch so a mutation landing mid-round-trip skips re-fetching.
  state.inflight = true;
  const list = document.querySelector('results-list');
  const sectionId = list && list.getAttribute('section-id');
  const grid = getGrid();
  const perPage = cfg.perPage || (grid ? getCards(grid).length : 0);

  findPage(parsed.handle, perPage, cfg)
    .then((page) => {
      if (!sectionId) throw new Error('No section id on <results-list>');
      return fetch(collectionUrl(cfg, { page: String(page), section_id: sectionId }));
    })
    .then((res) => {
      if (!res.ok) throw new Error('Section returned ' + res.status);
      return res.text();
    })
    .then((html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const sourceGrid = getGrid(doc) || doc.querySelector('[ref="grid"]');
      const card = sourceGrid && findCardByHandle(sourceGrid, parsed.handle);
      if (!card) throw new Error('No card for ' + parsed.handle + ' on its page');
      state.cachedCard = card.cloneNode(true);
      state.inflight = false;

      // Re-check it isn't already there (a re-render may have added it meanwhile).
      const liveGrid = getGrid();
      if (!liveGrid || findCardByHandle(liveGrid, parsed.handle)) {
        if (liveGrid) pin(parsed, cfg, state);
        return;
      }
      state.pinning = true;
      try {
        const node = document.importNode(state.cachedCard, true);
        prioritizePinnedImage(node);
        moveToFront(liveGrid, node);
        applyVariant(node, parsed, cfg, state);
      } finally {
        state.pinning = false;
      }
    })
    .catch((err) => {
      state.failed = true;
      state.inflight = false;
      if (window.console) console.warn('[bmp-curated-first] could not pin product:', err.message);
    });
}

/**
 * Which page of this collection holds `handle`? Reads the collection's handle
 * list, in grid order, from templates/collection.bmp-first-index.liquid
 * (250 handles per request, the paginate maximum).
 */
function findPage(handle, perPage, cfg) {
  if (!perPage) return Promise.reject(new Error('Unknown products per page'));
  const readIndexPage = (n) =>
    fetch(collectionUrl(cfg, { view: 'bmp-first-index', page: String(n) }))
      .then((res) => {
        if (!res.ok) throw new Error('Index returned ' + res.status);
        return res.text();
      })
      .then((text) => JSON.parse(text.slice(text.indexOf('{'), text.lastIndexOf('}') + 1)));

  return readIndexPage(1).then((first) => {
    const pages = [Promise.resolve(first)];
    for (let n = 2; n <= Math.min(first.pages || 1, 20); n++) pages.push(readIndexPage(n));
    return Promise.all(pages).then((all) => {
      const handles = [].concat.apply([], all.map((p) => p.handles || []));
      const index = handles.indexOf(handle);
      if (index === -1) throw new Error(handle + ' is not in this collection');
      return Math.floor(index / perPage) + 1;
    });
  });
}

// ---- colour (variant) -------------------------------------------------------

/**
 * Show the ad's colour on the pinned card, once per card. After that, the
 * shopper's own swatch clicks stand; the re-render observer must not undo them.
 */
function applyVariant(card, parsed, cfg, state) {
  if (!parsed.variant || card.dataset.bmpVariantApplied === parsed.variant) return;
  card.dataset.bmpVariantApplied = parsed.variant;

  loadVariantInfo(parsed, state).then((info) => {
    if (!info || !card.isConnected) return;
    return selectSwatch(card, info).then((selected) => {
      if (selected) return;
      // No matching swatch on this card: point its links at the variant and show its image.
      setVariantOnLinks(card, parsed.handle, parsed.variant);
      if (cfg.swapImage && info.imageSrc) swapImage(card, info.imageSrc);
    });
  });
}

/** The ad variant's colour option and image, from /products/<handle>.js (fetched once). */
function loadVariantInfo(parsed, state) {
  if (!state.variantInfo) {
    state.variantInfo = fetch(shopifyRoot() + 'products/' + parsed.handle + '.js')
      .then((res) => {
        if (!res.ok) throw new Error('product.js ' + res.status);
        return res.json();
      })
      .then((product) => {
        const id = parseInt(parsed.variant, 10);
        const variant = (product.variants || []).filter((v) => v.id === id)[0];
        if (!variant) return null;
        const names = (product.options || []).map((o) => (typeof o === 'string' ? o : o.name));
        const colorIndex = names.findIndex((n) => /colou?r/i.test(n || ''));
        const options = variant.options || [variant.option1, variant.option2, variant.option3];
        return {
          colorName: colorIndex === -1 ? null : names[colorIndex],
          colorValue: colorIndex === -1 ? null : options[colorIndex],
          imageSrc: variant.featured_image && variant.featured_image.src,
        };
      })
      .catch(() => null); // non-fatal: the card still works, in its default colour
  }
  return state.variantInfo;
}

/**
 * Click the card's own swatch for the ad's colour, exactly as a shopper would.
 * Horizon's swatch picker then updates the card image, link and highlight.
 * Resolves true when the card has that swatch.
 */
function selectSwatch(card, info) {
  if (!info.colorValue) return Promise.resolve(false);
  const inputs = card.querySelectorAll('swatches-variant-picker-component input[type="radio"]');
  let target = null;
  for (let i = 0; i < inputs.length; i++) {
    const input = inputs[i];
    if (input.value !== info.colorValue) continue;
    if (info.colorName && input.name.indexOf(info.colorName + '-') !== 0) continue;
    target = input;
    break;
  }
  if (!target) return Promise.resolve(false);
  if (target.checked) return Promise.resolve(true);

  return whenDefined('swatches-variant-picker-component', 3000).then((defined) => {
    if (!defined || !target.isConnected) return false;
    // A click (not a bare change event) runs the same path as a real tap.
    // product-card.js ignores clicks on inputs, so this never navigates.
    window.requestAnimationFrame(() => target.click());
    return true;
  });
}

function whenDefined(name, timeoutMs) {
  if (!window.customElements) return Promise.resolve(false);
  if (window.customElements.get(name)) return Promise.resolve(true);
  return Promise.race([
    window.customElements.whenDefined(name).then(() => true),
    new Promise((resolve) => setTimeout(() => resolve(false), timeoutMs)),
  ]);
}

/**
 * Point every anchor in the card that links to this product at the variant
 * (replace or append `variant=`). Matched by handle, so links to OTHER products
 * stay untouched.
 */
function setVariantOnLinks(card, handle, variantId) {
  const links = card.querySelectorAll('a[href*="/products/"]');
  for (let i = 0; i < links.length; i++) {
    const link = links[i];
    const href = link.getAttribute('href');
    if (!href || !linkMatchesHandle(href, handle)) continue;
    link.setAttribute('href', withVariantParam(href, variantId));
  }
}

/** Set/replace the `variant` query param on `href` using the URL API. */
function withVariantParam(href, variantId) {
  try {
    const url = new URL(href, window.location.origin);
    url.searchParams.set('variant', variantId);
    return url.pathname + url.search + url.hash;
  } catch (e) {
    return href;
  }
}

/**
 * Swap only the card's primary image (never the hover/carousel slides),
 * rebuilding srcset on the theme's width ladder.
 */
function swapImage(card, imageSrc) {
  if (card.dataset.bmpVariantSwapped === imageSrc) return;
  const img =
    card.querySelector('.card-gallery img') ||
    card.querySelector('.product-media-container img') ||
    card.querySelector('img');
  if (!img) return;

  let base;
  try {
    base = new URL(imageSrc, window.location.href);
  } catch (e) {
    return; // Not a rewritable URL: leave the existing image alone.
  }
  base.searchParams.delete('v');

  const widths = [240, 352, 480, 640, 832, 1200, 1600, 1920];
  img.setAttribute('src', resizeSrc(base, 640));
  img.setAttribute('srcset', widths.map((w) => resizeSrc(base, w) + ' ' + w + 'w').join(', '));
  img.removeAttribute('data-src');
  card.dataset.bmpVariantSwapped = imageSrc;
}

/** Clone `base` and set its `width` param; never mutates the shared base URL. */
function resizeSrc(base, width) {
  const url = new URL(base.href);
  url.searchParams.set('width', String(width));
  return url.toString();
}

// ---- re-render survival -----------------------------------------------------

function observeReRenders(parsed, cfg, state) {
  // Subtree on <results-list> (not just the grid): filter/sort re-renders replace
  // the grid's contents, and a grid-scoped observer would miss that. Mutations
  // are coalesced into one pin() per animation frame.
  const list = document.querySelector('results-list');
  if (!list || typeof MutationObserver === 'undefined') return;
  const observer = new MutationObserver(() => {
    if (state.pinning || state.rafId) return;
    state.rafId = window.requestAnimationFrame(() => {
      state.rafId = 0;
      pin(parsed, cfg, state);
    });
  });
  observer.observe(list, { childList: true, subtree: true });
}
